class BWDProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;
    this.url = this.dataset.url;
    this.intent = this.dataset.intent || 'related';
    this.limit = this.dataset.limit || '4';
    this.loaded = false;
    
    // Set up intersection observer for lazy loading
    this.intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        
        observer.disconnect();
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
  }

  connectedCallback() {
    // Start observing when element is connected to DOM
    this.intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    // Clean up observer if element is removed
    this.intersectionObserver.disconnect();
  }

  async loadRecommendations() {
    // Prevent duplicate loads
    if (this.loaded || !this.productId) return;
    this.loaded = true;

    try {
      // Build the recommendations URL with parameters
      const params = new URLSearchParams({
        section_id: 'bwd-product-recommendations-ajax',
        product_id: this.productId,
        limit: this.limit,
        intent: this.intent
      });
      
      const fullUrl = `${this.url}?${params.toString()}`;
      
      // Fetch recommendations
      const response = await fetch(fullUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch recommendations: ${response.status}`);
      }
      
      const html = await response.text();
      
      // Parse the response HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the recommendations content
      const recommendationsContent = doc.querySelector('.ysw-more-products__grid');
      const placeholder = this.querySelector('.bwd-recommendations-placeholder');
      
      if (!placeholder) return;
      
      if (recommendationsContent && recommendationsContent.children.length > 0) {
        // Clear placeholder and add recommendations
        placeholder.innerHTML = '';
        
        // Create the grid container
        const gridContainer = document.createElement('div');
        gridContainer.className = 'ysw-more-products__grid';
        gridContainer.innerHTML = recommendationsContent.innerHTML;
        
        placeholder.appendChild(gridContainer);
        
        // Initialize any product grid items (quick shop, etc.)
        this.initializeProductItems();
        
        // Dispatch custom event for analytics or other integrations
        document.dispatchEvent(
          new CustomEvent('bwd:recommendations:loaded', {
            detail: {
              section: this,
              productId: this.productId,
              intent: this.intent,
              count: recommendationsContent.children.length
            }
          })
        );
      } else {
        // No recommendations found - hide the section
        this.style.display = 'none';
        
        // Dispatch event for no recommendations
        document.dispatchEvent(
          new CustomEvent('bwd:recommendations:empty', {
            detail: {
              section: this,
              productId: this.productId,
              intent: this.intent
            }
          })
        );
      }
    } catch (error) {
      console.error('Error loading product recommendations:', error);
      
      // Hide section on error
      this.style.display = 'none';
      
      // Dispatch error event
      document.dispatchEvent(
        new CustomEvent('bwd:recommendations:error', {
          detail: {
            section: this,
            productId: this.productId,
            error: error.message
          }
        })
      );
    }
  }

  initializeProductItems() {
    // Dispatch the recommendations:loaded event that theme.js listens for
    // This will automatically initialize QuickAdd and QuickShop functionality
    document.dispatchEvent(
      new CustomEvent('recommendations:loaded', {
        detail: {
          section: this
        }
      })
    );
    
    // Also call reinitProductGridItem for additional initialization (reviews, etc.)
    if (window.theme && typeof window.theme.reinitProductGridItem === 'function') {
      window.theme.reinitProductGridItem(this);
    }
    
    // Initialize any Alpine.js components in the new content
    if (window.Alpine) {
      window.Alpine.initTree(this);
    }
  }
}

// Define the custom element
customElements.define('bwd-product-recommendations', BWDProductRecommendations);

// Export for potential use in other modules
export default BWDProductRecommendations;